import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";

const useStyles = makeStyles((theme) => {
  const baseSelectStyles = customSelectStyle(theme, "#1976d2");

  return {
    ...baseSelectStyles,
    selectFormControl: {
      ...baseSelectStyles.selectFormControl,
      margin: "7px 0 7px 0 !important",
      "& .MuiInputLabel-root": {
        "&.Mui-focused": {
          color: (props) => `${props.clientColor || "#1976d2"} !important`,
        },
      },
      "& > div": {
        "&:before": {
          borderBottomWidth: "1px !important",
          borderBottomColor: `${grayColor[4]} !important`,
        },
        "&:after": {
          borderBottomColor: (props) =>
            `${props.clientColor || "#1976d2"} !important`,
        },
      },
    },
    selectMenuItem: {
      ...baseSelectStyles.selectMenuItem,
      "&:hover": {
        ...baseSelectStyles.selectMenuItem["&:hover"],
        backgroundColor: (props) => props.clientColor || "#1976d2",
      },
    },
    selectMenuItemSelected: {
      ...baseSelectStyles.selectMenuItemSelected,
      backgroundColor: (props) =>
        `${props.clientColor || "#1976d2"} !important`,
    },
    selectMenuItemSelectedMultiple: {
      ...baseSelectStyles.selectMenuItemSelectedMultiple,
      backgroundColor: `${grayColor[12]} !important`,
      color: `${grayColor[2]} !important`,
      "&:hover": {
        ...baseSelectStyles.selectMenuItemSelectedMultiple["&:hover"],
        backgroundColor: (props) =>
          `${props.clientColor || "#1976d2"} !important`,
        color: "#fff !important",
      },
    },
    multiSelectMenuItem: {
      minHeight: 36,
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      color: `${grayColor[2]} !important`,
      whiteSpace: "nowrap",
      "&:hover": {
        color: "#fff !important",
        "& .MuiListItemText-primary": {
          color: "#fff !important",
        },
        "& .MuiCheckbox-root": {
          color: "#fff !important",
        },
      },
      "&$selectMenuItemSelectedMultiple": {
        "& .MuiListItemText-primary": {
          color: `${grayColor[2]} !important`,
        },
        "&:hover .MuiListItemText-primary": {
          color: "#fff !important",
        },
      },
    },
    multiSelectCheckbox: {
      padding: 0,
      marginRight: theme.spacing(0.5),
      "& svg": {
        width: 18,
        height: 18,
      },
    },
    multiSelectText: {
      margin: 0,
      overflow: "hidden",
      "& .MuiListItemText-primary": {
        color: `${grayColor[2]} !important`,
        fontSize: ".75rem",
        lineHeight: 1.3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
    selectedValue: {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: `${grayColor[2]} !important`,
      fontSize: ".75rem",
      lineHeight: "20px",
    },
    dialogTitle: {
      color: grayColor[2],
      fontSize: "1rem",
      fontWeight: 400,
      padding: theme.spacing(3, 3, 1),
    },
    dialogContent: {
      padding: theme.spacing(2, 3),
    },
    section: {
      marginBottom: theme.spacing(2.5),
      paddingBottom: theme.spacing(1.5),
      "&:last-child": {
        marginBottom: 0,
        paddingBottom: 0,
      },
    },
    sectionTitle: {
      fontSize: "0.75rem",
      fontWeight: 600,
      color: grayColor[2],
      textTransform: "uppercase",
      marginBottom: theme.spacing(1),
    },
    field: {
      marginBottom: theme.spacing(1.5),
    },
    compactField: {
      marginBottom: theme.spacing(1.5),
      maxWidth: 120,
    },
    activeField: {
      display: "flex",
      alignItems: "flex-end",
      height: 60,
      marginBottom: theme.spacing(1.5),
    },
    fieldLabel: {
      fontSize: "12px",
      fontWeight: 400,
      color: `${grayColor[1]} !important`,
      textTransform: "uppercase",
      marginBottom: 0,
      lineHeight: 1.2,
    },
    row: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      columnGap: theme.spacing(2.5),
      rowGap: theme.spacing(1),
      alignItems: "start",
    },
    mappingArea: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 56px minmax(0, 1fr)",
      columnGap: theme.spacing(2),
      alignItems: "center",
      marginBottom: theme.spacing(2.5),
    },
    mappingCard: {
      border: `1px solid ${grayColor[5]}`,
      borderRadius: 12,
      padding: theme.spacing(1.75, 3, 2),
      backgroundColor: "#fff",
    },
    mappingCardTitle: {
      color: grayColor[2],
      fontSize: "1rem",
      fontWeight: 500,
      textAlign: "center",
      marginBottom: theme.spacing(1),
    },
    mappingConnector: {
      color: (props) => props.clientColor || "#1976d2",
      fontSize: "1.35rem",
      fontWeight: 600,
      textAlign: "center",
    },
    readOnlyField: {
      margin: "7px 0 7px 0 !important",
      "& .MuiInputLabel-root": {
        fontSize: "12px",
        color: `${grayColor[1]} !important`,
      },
      "& .MuiInputBase-input": {
        fontSize: ".75rem",
        color: `${grayColor[2]} !important`,
        padding: "12px 0 7px",
      },
      "& .MuiInput-underline:before": {
        borderBottomColor: `${grayColor[4]} !important`,
      },
    },
    behaviorGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      columnGap: theme.spacing(2.5),
      rowGap: theme.spacing(1),
      alignItems: "start",
    },
    orderInput: {
      marginTop: theme.spacing(0.25),
      "& .MuiInputBase-input": {
        fontSize: ".75rem",
        color: grayColor[2],
        padding: "12px 0 7px",
        textAlign: "center",
      },
      "& .MuiInput-underline:before": {
        borderBottomColor: `${grayColor[4]} !important`,
      },
      "& .MuiInput-underline:after": {
        borderBottomColor: (props) =>
          `${props.clientColor || "#1976d2"} !important`,
      },
    },
    checkbox: {
      color: (props) => props.clientColor || "#1976d2",
      "&$checked": {
        color: (props) => props.clientColor || "#1976d2",
      },
    },
    checked: {},
    actions: {
      padding: theme.spacing(1, 3, 2),
    },
    saveButton: {
      backgroundColor: (props) => props.clientColor || "#1976d2",
      color: "#fff",
      boxShadow: "none",
      "&:hover": {
        backgroundColor: (props) => props.clientColor || "#1565c0",
        boxShadow: "none",
      },
      "&:disabled": {
        backgroundColor: grayColor[5],
        color: "#fff",
      },
    },
    deleteButton: {
      color: "#d32f2f",
      borderColor: "#d32f2f",
      "&:hover": {
        borderColor: "#b71c1c",
        backgroundColor: "rgba(211, 47, 47, 0.04)",
      },
    },
    actionsLeft: {
      marginRight: "auto",
    },
  };
});

const emptyForm = {
  sanitization_action: "replace_column",
  target_column: "",
  target_match_key: [],
  source_table: "",
  source_column: "",
  source_match_key: [],
  source_variant_strategy: "latest",
  source_variant_id: "",
  on_unmatched: "error",
  on_duplicate_source: "error",
  match_case_sensitive: true,
  is_active: true,
  rule_order: 1,
};

function getColumns(inputColumns, tableId) {
  const columns = inputColumns?.[tableId]?.columns || {};
  return Object.entries(columns).map(([columnId, metadata]) => ({
    column_id: columnId,
    column_name: metadata?.column_name || columnId,
  }));
}

function asArray(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (value) {
    return [value];
  }
  return [];
}

export default function SanitizationRuleDialog({
  open,
  rule,
  selectedInputTable,
  inputColumns,
  allInputMetadata,
  allVariants,
  clientColor,
  onClose,
  onSave,
  onDelete,
}) {
  const classes = useStyles({ clientColor });
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });
  const [form, setForm] = useState(emptyForm);

  const tableOptions = useMemo(() => {
    return Object.keys(inputColumns || {}).map((tableId) => ({
      table_id: tableId,
      table_name:
        allInputMetadata?.[tableId]?.table_description ||
        allInputMetadata?.[tableId]?.table_name ||
        inputColumns?.[tableId]?.table_name ||
        tableId,
    }));
  }, [allInputMetadata, inputColumns]);

  const targetColumns = useMemo(
    () => getColumns(inputColumns, selectedInputTable),
    [inputColumns, selectedInputTable],
  );

  const sourceColumns = useMemo(
    () => getColumns(inputColumns, form.source_table),
    [inputColumns, form.source_table],
  );

  const sourceVariantOptions = useMemo(() => {
    return allVariants?.[form.source_table] || [];
  }, [allVariants, form.source_table]);

  const targetTableLabel = useMemo(() => {
    return (
      allInputMetadata?.[selectedInputTable]?.table_description ||
      allInputMetadata?.[selectedInputTable]?.table_name ||
      inputColumns?.[selectedInputTable]?.table_name ||
      selectedInputTable
    );
  }, [allInputMetadata, inputColumns, selectedInputTable]);

  useEffect(() => {
    if (!open) return;

    if (rule) {
      const config = rule.rule_config || {};
      const sourceVariant = config.source_variant || { strategy: "latest" };
      const matchNormalization = config.match_normalization || {};
      setForm({
        sanitization_action: rule.sanitization_action || "replace_column",
        target_column: config.target_column || "",
        target_match_key: asArray(config.target_match_key),
        source_table: config.source_table || "",
        source_column: config.source_column || "",
        source_match_key: asArray(config.source_match_key),
        source_variant_strategy: sourceVariant.strategy || "latest",
        source_variant_id: sourceVariant.variant_id || "",
        on_unmatched: config.on_unmatched || "error",
        on_duplicate_source: config.on_duplicate_source || "error",
        match_case_sensitive: matchNormalization.case_sensitive !== false,
        is_active: rule.is_active !== false,
        rule_order: rule.rule_order || 1,
      });
      return;
    }

    setForm(emptyForm);
  }, [open, rule]);

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === "source_table"
        ? {
            source_column: "",
            source_match_key: [],
            source_variant_strategy: "latest",
            source_variant_id: "",
          }
        : {}),
      ...(field === "source_variant_strategy" && value === "latest"
        ? { source_variant_id: "" }
        : {}),
    }));
  };

  const handleSave = () => {
    const selectedSourceVariant = sourceVariantOptions.find(
      (variant) =>
        String(variant.variant_id) === String(form.source_variant_id),
    );

    onSave({
      rule_id: rule?.rule_id,
      table_id: selectedInputTable,
      sanitization_action: form.sanitization_action,
      is_active: form.is_active,
      rule_order: Number(form.rule_order) || 1,
      rule_config: {
        target_table: selectedInputTable,
        target_column: form.target_column,
        target_match_key: form.target_match_key,
        source_table: form.source_table,
        source_column: form.source_column,
        source_match_key: form.source_match_key,
        source_variant:
          form.source_variant_strategy === "specific"
            ? {
                strategy: "specific",
                variant_id: Number(form.source_variant_id),
                variant_name: selectedSourceVariant?.variant_name || "",
              }
            : { strategy: "latest" },
        on_unmatched: form.on_unmatched,
        on_duplicate_source: form.on_duplicate_source,
        match_normalization: {
          case_sensitive: form.match_case_sensitive,
          trim: true,
        },
      },
    });
  };

  const isInvalid =
    !form.target_column ||
    form.target_match_key.length === 0 ||
    !form.source_table ||
    !form.source_column ||
    form.source_match_key.length === 0 ||
    (form.source_variant_strategy === "specific" && !form.source_variant_id) ||
    form.target_match_key.length !== form.source_match_key.length;

  const renderColumnLabel = (selected, options) => {
    return selected
      .map((columnId) => {
        const column = options.find((option) => option.column_id === columnId);
        return column?.column_name || columnId;
      })
      .join(", ");
  };

  const renderColumnValue = (selected, options) => (
    <span className={classes.selectedValue}>
      {renderColumnLabel(selected, options)}
    </span>
  );

  const getSelectMenuProps = () => ({
    className: classes.selectMenu,
    classes: {
      paper: classes.selectPaper,
    },
  });

  const renderSelect = ({
    label,
    value,
    onChange,
    disabled = false,
    multiple = false,
    renderValue,
    children,
  }) => (
    <FormControl fullWidth className={classes.selectFormControl}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        onChange={onChange}
        disabled={disabled}
        multiple={multiple}
        renderValue={renderValue}
        label={label}
        MenuProps={getSelectMenuProps()}
      >
        {children}
      </Select>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle className={classes.dialogTitle}>
        {rule ? t("edit_sanitization_rule") : t("add_sanitization_rule")}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {t("sanitization_basic_config")}
          </Typography>
          <Box className={classes.row}>
            <Box className={classes.field}>
              {renderSelect({
                label: t("sanitization_action"),
                value: form.sanitization_action,
                onChange: (event) =>
                  updateField("sanitization_action", event.target.value),
                children: [
                  <MenuItem
                    key="replace_column"
                    value="replace_column"
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {t("sanitization_replace_column")}
                  </MenuItem>,
                ],
              })}
            </Box>
            <Box className={classes.compactField}>
              <Typography className={classes.fieldLabel}>
                {t("rule_order")}
              </Typography>
              <TextField
                className={classes.orderInput}
                type="number"
                value={form.rule_order}
                onChange={(event) =>
                  updateField("rule_order", event.target.value)
                }
                fullWidth
              />
            </Box>
            <Box className={classes.activeField}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_active}
                    onChange={(event) =>
                      updateField("is_active", event.target.checked)
                    }
                    classes={{
                      root: classes.checkbox,
                      checked: classes.checked,
                    }}
                  />
                }
                label={t("sanitization_rule_active")}
              />
            </Box>
          </Box>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {t("sanitization_specific_config")}
          </Typography>
          <Box className={classes.mappingArea}>
            <Box className={classes.mappingCard}>
              <Typography className={classes.mappingCardTitle}>
                {t("sanitization_target_config")}
              </Typography>
              <TextField
                className={classes.readOnlyField}
                label={t("target_table")}
                value={targetTableLabel}
                disabled
                fullWidth
              />
              {renderSelect({
                label: t("target_column"),
                value: form.target_column,
                onChange: (event) =>
                  updateField("target_column", event.target.value),
                children: targetColumns.map((column) => (
                  <MenuItem
                    key={column.column_id}
                    value={column.column_id}
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {column.column_name}
                  </MenuItem>
                )),
              })}
              {renderSelect({
                label: t("target_match_key"),
                multiple: true,
                value: form.target_match_key,
                onChange: (event) =>
                  updateField("target_match_key", event.target.value),
                renderValue: (selected) =>
                  renderColumnValue(selected, targetColumns),
                children: targetColumns.map((column) => (
                  <MenuItem
                    key={column.column_id}
                    value={column.column_id}
                    className={`${classes.selectMenuItem} ${classes.multiSelectMenuItem}`}
                    classes={{
                      selected: classes.selectMenuItemSelectedMultiple,
                    }}
                  >
                    <Checkbox
                      checked={form.target_match_key.includes(column.column_id)}
                      classes={{
                        root: `${classes.checkbox} ${classes.multiSelectCheckbox}`,
                        checked: classes.checked,
                      }}
                    />
                    <ListItemText
                      primary={column.column_name}
                      className={classes.multiSelectText}
                    />
                  </MenuItem>
                )),
              })}
            </Box>

            <Typography className={classes.mappingConnector}>↔</Typography>

            <Box className={classes.mappingCard}>
              <Typography className={classes.mappingCardTitle}>
                {t("sanitization_source_config")}
              </Typography>
              {renderSelect({
                label: t("source_table"),
                value: form.source_table,
                onChange: (event) =>
                  updateField("source_table", event.target.value),
                children: tableOptions.map((table) => (
                  <MenuItem
                    key={table.table_id}
                    value={table.table_id}
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {table.table_name}
                  </MenuItem>
                )),
              })}
              {renderSelect({
                label: t("source_column"),
                value: form.source_column,
                onChange: (event) =>
                  updateField("source_column", event.target.value),
                disabled: !form.source_table,
                children: sourceColumns.map((column) => (
                  <MenuItem
                    key={column.column_id}
                    value={column.column_id}
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {column.column_name}
                  </MenuItem>
                )),
              })}
              {renderSelect({
                label: t("source_match_key"),
                multiple: true,
                value: form.source_match_key,
                onChange: (event) =>
                  updateField("source_match_key", event.target.value),
                renderValue: (selected) =>
                  renderColumnValue(selected, sourceColumns),
                disabled: !form.source_table,
                children: sourceColumns.map((column) => (
                  <MenuItem
                    key={column.column_id}
                    value={column.column_id}
                    className={`${classes.selectMenuItem} ${classes.multiSelectMenuItem}`}
                    classes={{
                      selected: classes.selectMenuItemSelectedMultiple,
                    }}
                  >
                    <Checkbox
                      checked={form.source_match_key.includes(column.column_id)}
                      classes={{
                        root: `${classes.checkbox} ${classes.multiSelectCheckbox}`,
                        checked: classes.checked,
                      }}
                    />
                    <ListItemText
                      primary={column.column_name}
                      className={classes.multiSelectText}
                    />
                  </MenuItem>
                )),
              })}
            </Box>
          </Box>

          <Box className={classes.behaviorGrid}>
            <Box className={classes.field}>
              {renderSelect({
                label: t("match_case_sensitive"),
                value: String(form.match_case_sensitive),
                onChange: (event) =>
                  updateField(
                    "match_case_sensitive",
                    event.target.value === "true",
                  ),
                children: [
                  <MenuItem
                    key="true"
                    value="true"
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {t("match_case_sensitive_true")}
                  </MenuItem>,
                  <MenuItem
                    key="false"
                    value="false"
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {t("match_case_sensitive_false")}
                  </MenuItem>,
                ],
              })}
            </Box>
            <Box className={classes.field}>
              {renderSelect({
                label: t("on_unmatched"),
                value: form.on_unmatched,
                onChange: (event) =>
                  updateField("on_unmatched", event.target.value),
                children: [
                  <MenuItem
                    key="error"
                    value="error"
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {t("on_unmatched_error")}
                  </MenuItem>,
                  <MenuItem
                    key="keep_original"
                    value="keep_original"
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {t("on_unmatched_keep_original")}
                  </MenuItem>,
                ],
              })}
            </Box>
            <Box className={classes.field}>
              {renderSelect({
                label: t("on_duplicate_source"),
                value: form.on_duplicate_source,
                onChange: (event) =>
                  updateField("on_duplicate_source", event.target.value),
                children: [
                  <MenuItem
                    key="error"
                    value="error"
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {t("on_duplicate_source_error")}
                  </MenuItem>,
                  <MenuItem
                    key="first"
                    value="first"
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {t("on_duplicate_source_first")}
                  </MenuItem>,
                ],
              })}
            </Box>
            <Box className={classes.field}>
              {renderSelect({
                label: t("source_variant"),
                value: form.source_variant_strategy,
                onChange: (event) =>
                  updateField("source_variant_strategy", event.target.value),
                disabled: !form.source_table,
                children: [
                  <MenuItem
                    key="latest"
                    value="latest"
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {t("source_variant_latest")}
                  </MenuItem>,
                  <MenuItem
                    key="specific"
                    value="specific"
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {t("source_variant_specific")}
                  </MenuItem>,
                ],
              })}
            </Box>
            <Box className={classes.field}>
              {renderSelect({
                label: t("source_variant_specific_label"),
                value: form.source_variant_id,
                onChange: (event) =>
                  updateField("source_variant_id", event.target.value),
                disabled:
                  !form.source_table ||
                  form.source_variant_strategy !== "specific",
                children: sourceVariantOptions.map((variant) => (
                  <MenuItem
                    key={variant.variant_id}
                    value={variant.variant_id}
                    className={classes.selectMenuItem}
                    classes={{ selected: classes.selectMenuItemSelected }}
                  >
                    {variant.variant_name}
                  </MenuItem>
                )),
              })}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        {rule?.rule_id && (
          <Button
            className={`${classes.deleteButton} ${classes.actionsLeft}`}
            variant="outlined"
            onClick={() => onDelete(rule.rule_id)}
          >
            {t("delete_sanitization_rule")}
          </Button>
        )}
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button
          className={classes.saveButton}
          variant="contained"
          onClick={handleSave}
          disabled={isInvalid}
        >
          {t("save_changes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SanitizationRuleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  rule: PropTypes.object,
  selectedInputTable: PropTypes.string.isRequired,
  inputColumns: PropTypes.object,
  allInputMetadata: PropTypes.object,
  allVariants: PropTypes.object,
  clientColor: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
